import { useState } from "react";
import { useTranslation } from "react-i18next";
import { loc } from "../../hero/mock";
import { DETAIL, type Lang, type ScheduleRow, type ScheduleStatus } from "./data";
import {
  AppBar,
  AppCard,
  Banner,
  Button,
  IconButton,
  Money,
  ProgressBar,
  StatusBadge,
  StickyFooter,
  TypeIcon,
} from "./kit";
import { ScreenHost } from "./ScreenHost";
import { useScript } from "./useScript";
import "./detail.css";

/** Handoff HowDetail: the banner's «Eslatish» is clicked at 1500 ms (toast at once). */
const T_REMIND = 1500;
/** Scripted finger on the action — released after one press beat. */
const T_RELEASE = 150;

const ST_KEY: Record<ScheduleStatus, "how.app.detail.st_paid" | "how.app.detail.st_late" | "how.app.detail.st_due"> = {
  paid: "how.app.detail.st_paid",
  late: "how.app.detail.st_late",
  due: "how.app.detail.st_due",
};

/** ds StatRow with a dot and a two-tone value ("$150 · To‘landi"); kit StatRow takes a plain string value. */
function ScheduleLine({ label, amount, status, statusText, divider }: { label: string; amount: string; status: ScheduleRow["status"]; statusText: string; divider: boolean }) {
  return (
    <div className={"hk-stat has-dot" + (divider ? " div" : "")}>
      <span className={"hk-stat-dot " + status} />
      <span className="hk-stat-l">{label}</span>
      <span className="hk-stat-v">
        {amount} <span className={"hk-det-st " + status}>· {statusText}</span>
      </span>
    </div>
  );
}

// Step 4 — Shartnoma #NS-1042, overdue (handoff how-screens.html HowDetail = ds/kit-detail.jsx KitDetail).
// Script: the reminder is sent from the danger banner — press on «Eslatish» + toast. Rest state
// (prerender, reduced motion) = the screen as is, no toast.
export function DetailScreen({ play, still }: { play: boolean; still: boolean }) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === "ru" ? "ru" : "uz";
  const [pressed, setPressed] = useState(false);

  const toast = useScript(play && !still, ({ later, toast: showToast }) => {
    later(() => {
      setPressed(true);
      showToast(t("hero.stack.toast_reminded"));
      later(() => setPressed(false), T_RELEASE);
    }, T_REMIND);
  });

  const id = "#" + DETAIL.id;
  const info: [string, string][] = [
    [t("how.app.detail.client"), DETAIL.client],
    [t("how.app.detail.phone"), DETAIL.phone],
    [t("how.app.detail.start"), DETAIL.start],
    [t("how.app.create.monthly"), DETAIL.monthly],
    [t("how.app.detail.number"), id],
  ];

  return (
    <ScreenHost toast={still ? null : toast}>
      <AppBar title={t("how.app.detail.title")} sub={id} trailing={<IconButton icon="i-more" />} />
      <div className="hk-scroll">
        <div className="hk-det-body">
          <AppCard danger className="hk-det-main">
            <div className="hk-det-who">
              <TypeIcon />
              <div className="hk-det-x">
                <div className="hk-det-nm">{DETAIL.client}</div>
                <div className="hk-det-sub">
                  {t("how.app.detail.type_tech")} · {DETAIL.item}
                </div>
              </div>
            </div>
            <StatusBadge label={t("how.app.detail.status_overdue")} />
            <div className="hk-det-sum">
              <span className="hk-det-sum-l">{t("how.app.detail.remaining")}</span>
              <Money value={DETAIL.remaining} size="hero" tone="negative" />
              <ProgressBar value={DETAIL.ratio} overdue />
              <div className="hk-det-paid">
                <span>
                  {t("how.app.detail.paid")} <b>{DETAIL.paid}</b> / {DETAIL.total}
                </span>
                <span>{t("hero.mock.months", { a: DETAIL.months[0], b: DETAIL.months[1] })}</span>
              </div>
            </div>
          </AppCard>
          <Banner tone="danger" title={t("hero.stack.days_late", { n: DETAIL.late })} action={t("hero.stack.remind")} pressed={!still && pressed}>
            {t("how.app.detail.late_text", { amount: DETAIL.monthly, date: loc(DETAIL.next, lang) })}
          </Banner>
          <AppCard
            title={t("how.app.detail.schedule")}
            trailing={<span className="hk-det-meta">{t("how.app.detail.schedule_meta", { n: DETAIL.months[1], amount: DETAIL.monthly })}</span>}
            className="tight"
          >
            <div className="hk-det-rows">
              {DETAIL.schedule.map((r, i) => (
                <ScheduleLine
                  key={r.n}
                  label={t("how.app.detail.row", { n: r.n, date: loc(r.date, lang) })}
                  amount={DETAIL.monthly}
                  status={r.status}
                  statusText={t(ST_KEY[r.status])}
                  divider={i < DETAIL.schedule.length - 1}
                />
              ))}
            </div>
            <div className="hk-det-more">{t("how.app.detail.more", { n: DETAIL.more })}</div>
          </AppCard>
          <AppCard title={t("how.app.detail.info")}>
            <div className="hk-det-box">
              {info.map(([k, v]) => (
                <div key={k} className="hk-det-kv">
                  <span className="hk-det-k">{k}</span>
                  <span className="hk-det-v">{v}</span>
                </div>
              ))}
            </div>
          </AppCard>
          <Button variant="secondary" icon="i-file">
            {t("how.app.detail.pdf")}
          </Button>
        </div>
      </div>
      <StickyFooter leading={<Button variant="secondary" square icon="i-bell" />}>
        <Button grow>{t("how.app.detail.accept_pay")}</Button>
      </StickyFooter>
    </ScreenHost>
  );
}
